import { useEffect, useMemo, type ReactNode } from 'react';

import { useRemoteConfigQuery } from '@/hooks/useRemoteConfig';
import { ALL_OFFLINE_DATASETS, startOfflineDatasetHydrator } from '@/lib/offline/datasetHydrator';
import { useTenantSlug } from '@/stores/tenantStore';
import {
  OfflineRuntimeContext,
  type OfflineRuntimeContextValue,
  getEmptyOfflineRuntimeFeatures,
} from '@/contexts/offlineRuntimeContext';

export const OfflineRuntimeProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const tenantSlug = useTenantSlug();
  const remoteConfigQuery = useRemoteConfigQuery({ tenantSlug });
  const { data, isLoading, isError, error, refetch } = remoteConfigQuery;
  const features = useMemo<Record<string, unknown>>(
    () => data?.features ?? getEmptyOfflineRuntimeFeatures(),
    [data?.features],
  );
  const offlineRuntimeEnabled = resolveOfflineRuntimeEnabled(features);

  useEffect(() => {
    if (!offlineRuntimeEnabled || !tenantSlug) {
      return undefined;
    }
    const hydrator = startOfflineDatasetHydrator({
      tenantSlug,
      datasets: ALL_OFFLINE_DATASETS,
    });
    return () => {
      hydrator.stop();
    };
  }, [offlineRuntimeEnabled, tenantSlug]);

  const value = useMemo<OfflineRuntimeContextValue>(
    () => ({
      enabled: offlineRuntimeEnabled,
      isLoading,
      error: isError ? (error ?? null) : null,
      features,
      refresh: () => {
        refetch().catch((refetchError) => {
          if (import.meta.env.DEV) {
            console.warn('[OfflineRuntimeProvider] refetch failed', refetchError);
          }
        });
      },
    }),
    [offlineRuntimeEnabled, isLoading, isError, error, features, refetch],
  );

  return <OfflineRuntimeContext.Provider value={value}>{children}</OfflineRuntimeContext.Provider>;
};

const resolveOfflineRuntimeEnabled = (features: Record<string, unknown>): boolean => {
  const offlineGroup = features.offline;
  if (offlineGroup && typeof offlineGroup === 'object' && !Array.isArray(offlineGroup)) {
    const groupRecord = offlineGroup as Record<string, unknown>;
    if (typeof groupRecord.runtime === 'boolean') {
      return groupRecord.runtime;
    }
    if (typeof groupRecord.enabled === 'boolean') {
      return groupRecord.enabled;
    }
  }

  const nested = features['offline.runtime'];
  if (typeof nested === 'boolean') {
    return nested;
  }
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const nestedRecord = nested as Record<string, unknown>;
    if (typeof nestedRecord.enabled === 'boolean') {
      return nestedRecord.enabled;
    }
    if (typeof nestedRecord.runtime === 'boolean') {
      return nestedRecord.runtime;
    }
  }

  return false;
};
